import React, { Fragment, useEffect, useMemo, useState } from 'react';
import StickyBox from 'react-sticky-box';
import Breadcrumb from '../Breadcrumb';
import Sidebar from '../Sidebar';
import axios from 'axios';
import { useNavigate, useParams } from 'react-router-dom';
import { useSelector } from 'react-redux';
import SecurePdfViewer from './SecurePdfViewer';
import { Button, DropdownItem, DropdownMenu, DropdownToggle, Modal, ModalBody, ModalFooter, ModalHeader, UncontrolledDropdown } from 'reactstrap';
import {
  GED_DOCUMENT_DETAIL_UUID,
  GED_DOCUMENT_DOWNLOAD,
  GED_DOCUMENT_EVENTS,
  GED_DOCUMENT_EVENTS_EXPORT,
  GED_DOCUMENT_INTEGRITY,
  GED_DOCUMENT_VIEW,
  GED_DOCUMENT_WORKFLOW,
} from '../../auth/urlParameters';
import { ToastConfirm, ToastError } from '../../../ui/AlertesUi';
import { SimpleSpinner } from '../../../ui/Spinner';
import './ArchiveStyles.css';

const formatFileSize = (bytes) => {
  if (!bytes && bytes !== 0) return '—';
  const sizes = ['B', 'KB', 'MB', 'GB'];
  const index = Math.min(Math.floor(Math.log(bytes) / Math.log(1024)), sizes.length - 1);
  const value = bytes / Math.pow(1024, index);
  return `${value.toFixed(value >= 10 || index === 0 ? 0 : 1)} ${sizes[index]}`;
};

const formatValue = (value) => {
  if (value === null || value === undefined || value === '') return '—';
  if (Array.isArray(value)) {
    return value.length ? value.join(', ') : '—';
  }
  if (typeof value === 'object') {
    return JSON.stringify(value);
  }
  return value.toString();
};

const hasValue = (value) => {
  if (value === null || value === undefined) return false;
  if (typeof value === 'string') return value.trim() !== '';
  if (Array.isArray(value)) return value.length > 0;
  if (typeof value === 'object') return Object.keys(value).length > 0;
  return true;
};

const eventTypeLabels = {
  created: 'Création',
  status_change: 'Changement de statut',
  version_added: 'Nouvelle version',
  integrity_baseline: 'Référence d’intégrité établie',
  metadata_update: 'Mise à jour des métadonnées',
  metadata_proposed: 'Correction de métadonnées proposée',
  metadata_rejected: 'Correction de métadonnées refusée',
  metadata_reviewed: 'Métadonnées vérifiées',
  updated: 'Modification',
  consultation: 'Consultation',
  trash: 'Mise en corbeille',
  delete: 'Suppression définitive',
  restore: 'Restauration',
  disposition_hold: 'Suspension de la disposition',
  disposition_hold_released: 'Levée de la suspension',
};

const formatEventType = (type) => eventTypeLabels[type] || type || '—';
const validationLabels = {
  draft: 'Brouillon',
  'En revue': 'En revue',
  'À corriger': 'À corriger',
  'Actif Conservée': 'Validée',
};
const conservationLabels = {
  pending: 'Pas encore en conservation',
  in_conservation: 'En conservation',
  transferred: 'Transférée',
  destroyed: 'Détruite',
};
const integrityLabels = {
  verified: 'Intégrité vérifiée',
  mismatch: 'Empreintes différentes',
  missing: 'Fichier introuvable',
  no_checksum: 'Référence d’intégrité à établir',
  no_file: 'Aucun fichier',
  unavailable: 'Vérification impossible',
};

const formatIntegrityDate = (value) => value ? new Date(value).toLocaleString('fr-FR') : '—';

const eventFieldLabels = {
  title: 'Titre',
  reference: 'Référence',
  classificationCode: 'Code de classification',
  metadata: 'Métadonnées',
  keywords: 'Mots-clés',
  folderId: 'Dossier',
  planId: 'Plan',
  archiveProfileId: 'Profil',
  regleConservationId: 'Règle de conservation',
  producerServiceId: 'Service producteur',
  status: 'Statut',
  channel: 'Canal',
  ip: 'Adresse IP',
  userAgent: 'User-Agent',
  versionNumber: 'Version',
  originalName: 'Nom original',
  fileSize: 'Taille du fichier',
  mimeType: 'Type MIME',
  source: 'Source',
  file: 'Fichier',
  basename: 'Nom du fichier',
  uuid: 'UUID',
  reason: 'Motif',
  proposedBy: 'Proposé par',
  proposedAt: 'Proposé le',
};

const formatEventField = (field) => eventFieldLabels[field] || field;

const formatEventMetadataValue = (key, value) => {
  if (key === 'fileSize') {
    return formatFileSize(Number(value));
  }
  return formatValue(value);
};

const formatEventStatus = (event) => {
  const previous = event?.previousState;
  const next = event?.newState;
  if (previous && next && previous !== next) {
    return `${previous} → ${next}`;
  }
  if (next) {
    return `Statut : ${next}`;
  }
  if (previous) {
    return `Ancien statut : ${previous}`;
  }
  return null;
};

const formatEventAuthor = (event) => {
  const author = event?.performedBy;
  if (!author) return '—';
  return author.name || author.username || '—';
};

const ArchiveDetails = () => {
  const { uuid } = useParams();
  const session = useSelector((state) => state.sessionAuth);
  const profile = useSelector((state) => state.userSettings);
  const navigate = useNavigate();
  const [doc, setDoc] = useState(null);
  const [loading, setLoading] = useState(true);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewUrl, setPreviewUrl] = useState('');
  const [previewBlob, setPreviewBlob] = useState(null);
  const [previewMime, setPreviewMime] = useState('');
  const [previewError, setPreviewError] = useState('');
  const [integrity, setIntegrity] = useState({ loading: false, result: null, error: '' });
  const [statusSaving, setStatusSaving] = useState(false);
  const [correctionOpen, setCorrectionOpen] = useState(false);
  const [correctionReason, setCorrectionReason] = useState('');
  const [rotation, setRotation] = useState(0);
  const [eventModalOpen, setEventModalOpen] = useState(false);
  const [sheetOpen, setSheetOpen] = useState(false);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const roles = Array.isArray(session.roles) ? session.roles : [];
  const accessFilters = profile?.accessFilters || {};
  const allowExport = accessFilters.allowExport !== false;
  const canDownload = roles.some((role) =>
    ['ROLE_ADMIN', 'ROLE_SUPER_ADMIN', 'ROLE_EDITED', 'ROLE_ASSIST'].includes(role)
  ) && allowExport;
  const canEdit = roles.some((role) =>
    ['ROLE_ADMIN', 'ROLE_SUPER_ADMIN', 'ROLE_EDITED', 'ROLE_ASSIST'].includes(role)
  );
  const canManageStatus = roles.some((role) =>
    ['ROLE_ADMIN', 'ROLE_SUPER_ADMIN', 'ROLE_EDITED'].includes(role)
  );
  const canView = roles.some((role) =>
    ['ROLE_ADMIN', 'ROLE_SUPER_ADMIN', 'ROLE_EDITED', 'ROLE_ASSIST', 'ROLE_USER'].includes(role)
  );
  const isAdmin = roles.some((role) => ['ROLE_ADMIN', 'ROLE_SUPER_ADMIN'].includes(role));

  const authHeaders = useMemo(
    () => ({
      headers: { Authorization: 'Bearer ' + session.token },
    }),
    [session.token]
  );

  useEffect(() => {
    let isMounted = true;
    setLoading(true);
    axios
      .get(GED_DOCUMENT_DETAIL_UUID(uuid), authHeaders)
      .then((res) => {
        if (!isMounted) return;
        setDoc(res.data || null);
      })
      .catch(() => {
        if (!isMounted) return;
        ToastError('Impossible de charger les details de l\'archive');
        setDoc(null);
      })
      .finally(() => {
        if (isMounted) setLoading(false);
      });

    return () => {
      isMounted = false;
    };
  }, [uuid, authHeaders]);

  useEffect(() => {
    if (!doc?.hasFile || !canView) {
      setPreviewUrl('');
      setPreviewBlob(null);
      setPreviewMime('');
      setPreviewError('');
      return;
    }
    let isMounted = true;
    setPreviewLoading(true);
    setPreviewError('');
    setPreviewBlob(null);
    axios
      .get(GED_DOCUMENT_VIEW(doc.id), { ...authHeaders, responseType: 'blob' })
      .then((res) => {
        if (!isMounted) return;
        const mime = res.data?.type || doc.latestVersion?.mimeType || '';
        const url = window.URL.createObjectURL(res.data);
        setPreviewUrl(url);
        setPreviewBlob(res.data);
        setPreviewMime(mime);
      })
      .catch(() => {
        if (!isMounted) return;
        setPreviewError('Impossible de charger l\'apercu.');
        setPreviewUrl('');
        setPreviewBlob(null);
      })
      .finally(() => {
        if (isMounted) setPreviewLoading(false);
      });

    return () => {
      isMounted = false;
    };
  }, [doc, authHeaders, canView]);

  useEffect(() => {
    if (!doc?.id) {
      setIntegrity({ loading: false, result: null, error: '' });
      return undefined;
    }
    let active = true;
    setIntegrity({ loading: true, result: null, error: '' });
    axios.get(GED_DOCUMENT_INTEGRITY(doc.id), authHeaders)
      .then(({ data }) => { if (active) setIntegrity({ loading: false, result: data, error: '' }); })
      .catch(() => { if (active) setIntegrity({ loading: false, result: null, error: 'Vérification indisponible.' }); });
    return () => { active = false; };
  }, [doc?.id, authHeaders]);

  const recheckIntegrity = async () => {
    if (!doc?.id) return;
    setIntegrity((current) => ({ ...current, loading: true, error: '' }));
    try {
      const { data } = await axios.get(GED_DOCUMENT_INTEGRITY(doc.id), authHeaders);
      setIntegrity({ loading: false, result: data, error: '' });
    } catch (_) {
      setIntegrity((current) => ({ ...current, loading: false, error: 'Vérification indisponible.' }));
    }
  };

  useEffect(() => {
    return () => {
      if (previewUrl) {
        window.URL.revokeObjectURL(previewUrl);
      }
    };
  }, [previewUrl]);

  const parseFilename = (headers, fallback) => {
    const disposition = headers?.['content-disposition'] || '';
    const match = disposition.match(/filename="(.+?)"/);
    if (match && match[1]) {
      return match[1];
    }
    return fallback || 'archive';
  };

  const downloadBlob = (blob, filename) => {
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    link.remove();
    setTimeout(() => window.URL.revokeObjectURL(url), 10000);
  };

  const handleDownload = async () => {
    if (!doc || !doc.hasFile || !canDownload) return;
    try {
      const res = await axios.get(GED_DOCUMENT_DOWNLOAD(doc.id), {
        ...authHeaders,
        responseType: 'blob',
      });
      const filename = parseFilename(res.headers, doc.title || 'archive');
      downloadBlob(res.data, filename);
    } catch (err) {
      ToastError('Impossible de télécharger le document');
    }
  };

  const handleExportEvents = async () => {
    if (!doc || !canDownload) return;
    try {
      const res = await axios.get(GED_DOCUMENT_EVENTS_EXPORT(doc.id), {
        ...authHeaders,
        responseType: 'blob',
      });
      const fallback = `traceabilite_${doc.reference || doc.id}.csv`;
      const filename = parseFilename(res.headers, fallback);
      downloadBlob(res.data, filename);
    } catch (err) {
      ToastError('Impossible d\'exporter la traçabilité.');
    }
  };

  const openEventDetails = (event = null) => {
    setSelectedEvent(event || null);
    setEventModalOpen(true);
  };

  const closeEventDetails = () => {
    setEventModalOpen(false);
    setSelectedEvent(null);
  };

  const handleStatusChange = async (status, comment = '') => {
    if (!doc || !canManageStatus) return;
    setStatusSaving(true);
    try {
      const response = await axios.post(
        GED_DOCUMENT_WORKFLOW(doc.id),
        { status, comment },
        authHeaders
      );
      setDoc((prev) => (prev ? { ...prev, ...response.data } : prev));
      try {
        const eventsResponse = await axios.get(GED_DOCUMENT_EVENTS(doc.id), authHeaders);
        setDoc((prev) => (prev ? { ...prev, lifeCycleEvents: eventsResponse.data?.events || prev.lifeCycleEvents } : prev));
      } catch (_) {
        // Le changement de statut est déjà enregistré ; la trace sera visible au prochain chargement.
      }
      setCorrectionOpen(false);
      setCorrectionReason('');
      ToastConfirm('Statut mis à jour');
    } catch (err) {
      ToastError(err.response?.data?.message || 'Impossible de mettre à jour le statut');
    } finally {
      setStatusSaving(false);
    }
  };

  const renderEventChanges = (changes) => {
    const entries = Object.entries(changes || {});
    if (entries.length === 0) return null;
    return (
      <ul className="archive-event-changes">
        {entries.map(([field, change]) => (
          <li key={field}>
            <span className="archive-event-field">{formatEventField(field)}</span>
            <span className="archive-event-change">
              <span className="archive-event-value">{formatValue(change?.from)}</span>
              <i className="ri-arrow-right-line"></i>
              <span className="archive-event-value">{formatValue(change?.to)}</span>
            </span>
          </li>
        ))}
      </ul>
    );
  };

  const formatProposer = (id) => {
    const event = lifeCycleEvents.find((item) => Number(item.performedBy?.id) === Number(id));
    return event ? formatEventAuthor(event) : 'Utilisateur inconnu';
  };

  const renderEventMetadata = (metadata) => {
    if (!metadata || Object.keys(metadata).length === 0) {
      return <div className="archive-empty">Aucun détail disponible.</div>;
    }
    const { changes, ...rest } = metadata;
    const restEntries = Object.entries(rest || {});
    return (
      <div className="archive-event-details">
        {changes && renderEventChanges(changes)}
        {restEntries.length > 0 && (
          <ul className="archive-event-meta">
            {restEntries.map(([key, value]) => (
              <li key={key}>
                <span>{formatEventField(key)}</span>
                <span>{key === 'proposedBy' ? formatProposer(value) : formatEventMetadataValue(key, value)}</span>
              </li>
            ))}
          </ul>
        )}
      </div>
    );
  };

  const ruleContextItems = useMemo(() => {
    const regle = doc?.regleConservation;
    if (!regle) return [];
    return [
      ['Code de la règle', regle.code || '—'],
      ['Description de la règle', regle.description || '—'],
      ['Déclencheur', regle.retentionTrigger === 'event' ? regle.triggerEventLabel || 'Événement métier' : 'Versement dans Syidocs'],
    ];
  }, [doc]);

  const retentionItems = useMemo(() => {
    const regle = doc?.regleConservation;
    if (!regle) return [];
    const duration = regle.isUnlimited
      ? 'Illimitée'
      : regle.retentionDurationYears
        ? `${regle.retentionDurationYears} an(s)`
        : '—';
    const destinationMap = {
      conservation_permanente: 'Conservation permanente',
      destruction_securisee: 'Destruction sécurisée',
      transfert: 'Transfert vers un autre service',
    };
    const destination =
      destinationMap[regle.finalDestination] || regle.finalDestination || '—';
    return [
      ['Durée de conservation', duration],
      ['Sort final', destination],
      ['Version de la règle', regle.version ? `v${regle.version}` : '—'],
      ['Responsable (direction/service)', regle.responsable || '—'],
    ];
  }, [doc]);

  const captureFieldKeys = useMemo(() => {
    const fields = doc?.archiveProfile?.captureFields;
    if (!Array.isArray(fields)) return new Set();
    return new Set(
      fields
        .map((field) => (typeof field?.key === 'string' ? field.key : null))
        .filter(Boolean)
    );
  }, [doc]);

  const ocrEntries = useMemo(() => {
    const fields = doc?.archiveProfile?.captureFields;
    const metadata = doc?.metadata;
    if (!Array.isArray(fields) || !metadata || typeof metadata !== 'object') return [];
    return fields.reduce((acc, field) => {
      const key = typeof field?.key === 'string' ? field.key : null;
      if (!key) return acc;
      const value = metadata[key];
      if (!hasValue(value)) return acc;
      const label = field?.label || key;
      acc.push([label, value]);
      return acc;
    }, []);
  }, [doc]);

  const lifeCycleEvents = useMemo(
    () => (doc?.lifeCycleEvents ? doc.lifeCycleEvents : []),
    [doc]
  );
  const previewEvents = useMemo(
    () => lifeCycleEvents.slice(0, 2),
    [lifeCycleEvents]
  );

  const customPropertyMetadata = useMemo(() => {
    const metadata = doc?.metadata;
    const result = {
      entries: [],
      skipKeys: new Set(),
      handledArray: false,
    };
    if (!metadata) return result;

    const addEntry = (map, key, value) => {
      const normalizedKey = typeof key === 'string' ? key.trim() : '';
      if (!normalizedKey || !hasValue(value)) return;
      map.set(normalizedKey, value);
    };

    const parseArrayItems = (items) => {
      const map = new Map();
      items.forEach((item) => {
        if (!item || typeof item !== 'object') return;
        const key = item.key ?? item.property ?? item.propriete ?? item.name ?? item.label;
        const value = item.value ?? item.valeur ?? item.data;
        addEntry(map, key, value);
      });
      return map;
    };

    if (Array.isArray(metadata)) {
      const map = parseArrayItems(metadata);
      if (map.size > 0) {
        result.entries = Array.from(map.entries());
        result.handledArray = true;
      }
      return result;
    }

    if (typeof metadata !== 'object') return result;

    const containers = [
      { key: 'properties', value: metadata.properties },
      { key: 'customFields', value: metadata.customFields },
      { key: 'customMetadata', value: metadata.customMetadata },
      { key: 'customProperties', value: metadata.customProperties },
      { key: 'proprietes', value: metadata.proprietes },
      { key: 'propertyValues', value: metadata.propertyValues },
      { key: '_custom', value: metadata._custom },
    ];

    const map = new Map();
    containers.forEach((container) => {
      const value = container.value;
      if (!value) return;
      const beforeSize = map.size;
      if (Array.isArray(value)) {
        const parsed = parseArrayItems(value);
        parsed.forEach((entryValue, entryKey) => {
          addEntry(map, entryKey, entryValue);
        });
      } else if (typeof value === 'object') {
        Object.entries(value).forEach(([entryKey, entryValue]) => {
          addEntry(map, entryKey, entryValue);
        });
      }
      if (map.size > beforeSize) {
        result.skipKeys.add(container.key);
      }
    });

    if (map.size > 0) {
      result.entries = Array.from(map.entries());
    }

    return result;
  }, [doc]);

  const metadataEntries = useMemo(() => {
    const metadata = doc?.metadata;
    if (!metadata || typeof metadata !== 'object') return [];
    if (Array.isArray(metadata)) {
      if (customPropertyMetadata.handledArray) return [];
      return metadata.map((value, index) => [`Element ${index + 1}`, value]);
    }
    return Object.entries(metadata).filter(([key]) => {
      const normalized = key.toString().toLowerCase();
      if (normalized.startsWith('plan_')) return false;
      if (normalized.startsWith('_')) return false;
      if (captureFieldKeys.has(key)) return false;
      if (customPropertyMetadata.skipKeys.has(key)) return false;
      return true;
    });
  }, [doc, captureFieldKeys, customPropertyMetadata]);

  const combinedMetadata = useMemo(() => {
    const entries = new Map();
    [...ocrEntries, ...customPropertyMetadata.entries, ...metadataEntries].forEach(([key, value]) => {
      if (!entries.has(key)) entries.set(key, value);
    });
    return Array.from(entries.entries());
  }, [ocrEntries, customPropertyMetadata, metadataEntries]);

  const previewKind = previewMime || doc?.latestVersion?.mimeType || '';
  const isPdf = previewKind.toLowerCase().includes('pdf');
  const isImage = previewKind.toLowerCase().startsWith('image/');
  const isRotatable = isImage && !!previewUrl;

  const planLabel = doc?.plan ? `${doc.plan.code} - ${doc.plan.title}` : '—';
  const folderLabel = doc?.folder
    ? `${doc.folder.reference ? `${doc.folder.reference} - ` : ''}${doc.folder.title}`
    : 'Racine';
  const profileLabel = doc?.archiveProfile
    ? `${doc.archiveProfile.code} - ${doc.archiveProfile.title}`
    : '—';
  const regleLabel = doc?.regleConservation?.regle || '—';
  const serviceLabel = doc?.producerService
    ? `${doc.producerService.code} - ${doc.producerService.title}`
    : '—';
  const confidentialityLabel = doc?.archiveProfile?.confidentialite || doc?.metadata?.plan_confidentialite || 'Non renseignée';
  const retentionRuleDiffersFromProfile = Boolean(
    doc?.archiveProfile?.defaultRetentionRuleId && doc?.regleConservationId &&
    Number(doc.archiveProfile.defaultRetentionRuleId) !== Number(doc.regleConservationId)
  );
  const isTrashed = doc?.status === 'En corbeille';
  const isDestroyed = doc?.status === 'Détruite';
  const validationStatus = doc?.validationStatus || doc?.status;
  const conservationStatus = doc?.conservationStatus || (validationStatus === 'Actif Conservée' ? 'in_conservation' : 'pending');
  const canSubmitReview = ['draft', 'À corriger', 'Actif Conservée'].includes(validationStatus);
  const canDecideReview = validationStatus === 'En revue';

  useEffect(() => {
    if (!isImage) {
      setRotation(0);
    }
  }, [isImage, previewUrl]);

  const rotateLeft = () => setRotation((prev) => (prev - 90 + 360) % 360);
  const rotateRight = () => setRotation((prev) => (prev + 90) % 360);
  const resetRotation = () => setRotation(0);

  return (
    <Fragment>
      <Breadcrumb title="Archives" submenu="Détail d'archive" />
      <div className="content">
        <div className="container-fluid">
          <div className="row">
            <div className="col-md-4 col-lg-3 col-xl-3 theiaStickySidebar">
              <StickyBox offsetTop={20} offsetBottom={20}>
                <Sidebar />
              </StickyBox>
            </div>
            <div className="col-md-8 col-lg-9 col-xl-9">
              <div className="archive-detail-header">
                <div>
                  <h4 className="htitle">{doc?.title || 'Archive'}</h4>
                  <p className="text-muted mb-0">
                    {doc?.reference ? `Référence : ${doc.reference}` : '—'}
                  </p>
                </div>
                <div className="archive-detail-actions" id="archive-detail-action-bar">
                  <button
                    className="btn archive-action-back"
                    type="button"
                    onClick={() => navigate('/apps/in/archives')}
                  >
                    <i className="ri-arrow-left-line" aria-hidden="true" />
                    Retour à la liste
                  </button>
                  {!isDestroyed && (
                    <button
                      className="btn archive-action-edit"
                      type="button"
                      onClick={() => {
                        if (!doc?.uuid) {
                          ToastError('UUID introuvable pour ce document.');
                          return;
                        }
                        navigate(`/apps/in/archives/add?docUuid=${doc.uuid}`);
                      }}
                      disabled={!canEdit || !doc || isTrashed}
                    >
                      <i className="ri-pencil-line" aria-hidden="true" />
                      Éditer
                    </button>
                  )}
                  {canManageStatus && !isTrashed && !isDestroyed && (canSubmitReview || canDecideReview) && (
                    <div className="archive-detail-status-actions" role="group" aria-label="Statut de l'archive">
                      <span className="archive-detail-status-label">Statut</span>
                      <UncontrolledDropdown className="archive-status-dropdown">
                        <DropdownToggle className="archive-status-trigger" disabled={statusSaving} aria-label="Choisir une action sur le statut de l'archive">
                          <i className="ri-exchange-line" aria-hidden="true" />
                          Changer le statut
                          <i className="ri-arrow-down-s-line archive-status-chevron" aria-hidden="true" />
                        </DropdownToggle>
                        <DropdownMenu end className="archive-status-menu">
                          <DropdownItem header>Statut actuel : {validationLabels[validationStatus] || formatValue(validationStatus)}</DropdownItem>
                          {canSubmitReview && (
                            <DropdownItem onClick={() => handleStatusChange('En revue')} disabled={statusSaving}>
                              <i className="ri-time-line" aria-hidden="true" />
                              <span>Mettre en revue</span>
                            </DropdownItem>
                          )}
                          {canDecideReview && (
                            <DropdownItem onClick={() => handleStatusChange('Actif Conservée')} disabled={statusSaving || (doc?.validationBlockers || []).length > 0}>
                              <i className="ri-checkbox-circle-line" aria-hidden="true" />
                              <span>Valider l’archive</span>
                            </DropdownItem>
                          )}
                          {canDecideReview && (doc?.validationBlockers || []).length > 0 && (
                            <DropdownItem text className="archive-status-menu-hint">{doc.validationBlockers.join(' · ')}</DropdownItem>
                          )}
                          {canDecideReview && (
                            <DropdownItem onClick={() => setCorrectionOpen(true)} disabled={statusSaving}>
                              <i className="ri-error-warning-line" aria-hidden="true" />
                              <span>Demander une correction</span>
                            </DropdownItem>
                          )}
                        </DropdownMenu>
                      </UncontrolledDropdown>
                    </div>
                  )}
                  {!isDestroyed && (
                    <button
                      className="btn archive-action-download"
                      type="button"
                      onClick={handleDownload}
                      disabled={!canDownload || !doc?.hasFile || isTrashed}
                      aria-label="Télécharger l’archive"
                      title="Télécharger l’archive"
                    >
                      <i className="ri-download-2-line" aria-hidden="true" />
                    </button>
                  )}
                  {!isDestroyed && <button
                    className="btn archive-action-sheet"
                    type="button"
                    onClick={() => setSheetOpen(true)}
                    disabled={!doc || !canView || !canDownload || isTrashed}
                    aria-label="Ouvrir la fiche récapitulative imprimable"
                    title="Fiche récapitulative imprimable"
                  >
                    <i className="ri-file-list-3-line" aria-hidden="true" />
                  </button>}
                </div>
              </div>

              {loading ? (
                <div className="archive-loading">{SimpleSpinner()}</div>
              ) : !doc ? (
                <div className="archive-empty">Archive introuvable.</div>
              ) : (
                <div className="archive-detail-grid">
                  <div className="archive-detail-sidebar">
                    <section className="archive-detail-card archive-integrity-card" aria-labelledby="archive-integrity-heading">
                      <div className="archive-detail-card-header">
                        <h5 id="archive-integrity-heading">Intégrité du fichier</h5>
                        <button type="button" className="btn btn-outline-secondary btn-xs" onClick={recheckIntegrity} disabled={integrity.loading || !doc?.hasFile}>
                          <i className="ri-refresh-line" aria-hidden="true" /> {integrity.loading ? 'Contrôle…' : 'Vérifier'}
                        </button>
                      </div>
                      {integrity.loading && !integrity.result ? <p role="status">Vérification en cours…</p> : null}
                      {integrity.error && <p className="archive-integrity-error" role="alert">{integrity.error}</p>}
                      {integrity.result && <>
                        <p className={`archive-integrity-status ${integrity.result.status}`} role={['mismatch', 'missing'].includes(integrity.result.status) ? 'alert' : 'status'}>
                          <i className={integrity.result.status === 'verified' ? 'ri-checkbox-circle-line' : integrity.result.status === 'no_checksum' ? 'ri-information-line' : 'ri-error-warning-line'} aria-hidden="true" />
                          {integrity.result.status === 'verified' && integrity.result.baselineEstablishedAt
                            ? `Intégrité vérifiée depuis la référence du ${formatIntegrityDate(integrity.result.baselineEstablishedAt)}`
                            : integrityLabels[integrity.result.status] || 'État inconnu'}
                        </p>
                        {integrity.result.status === 'no_checksum' && <p className="archive-integrity-note">Aucune empreinte enregistrée pour cette version. Le contrôle ne peut pas encore conclure sur son intégrité.</p>}
                        <ul className="archive-detail-list archive-detail-list-compact">
                          {integrity.result.versionNumber != null && <li><span>Version contrôlée</span><span>v{integrity.result.versionNumber}</span></li>}
                          <li><span>{['no_checksum', 'no_file', 'unavailable'].includes(integrity.result.status) ? 'Dernière tentative' : 'Dernier contrôle'}</span><span>{formatIntegrityDate(integrity.result.checkedAt)}</span></li>
                        </ul>
                        {integrity.result.status === 'no_checksum' && (isAdmin
                          ? <button type="button" className="archive-integrity-regularize" onClick={() => navigate('/apps/in/archives/integrite-regularisation')}>Ouvrir la régularisation <i className="ri-arrow-right-line" aria-hidden="true" /></button>
                          : <p className="archive-integrity-note">Un administrateur peut établir une référence datée pour les archives anciennes.</p>)}
                        {(integrity.result.expectedChecksum || integrity.result.baselineEstablishedAt || integrity.result.actualChecksum) && <details className="archive-integrity-technical">
                          <summary>Données techniques du contrôle</summary>
                          <ul className="archive-detail-list archive-detail-list-compact">
                            {integrity.result.baselineEstablishedAt && <li><span>Référence établie le</span><span>{formatIntegrityDate(integrity.result.baselineEstablishedAt)}</span></li>}
                            {integrity.result.expectedChecksum && <li className="archive-integrity-hash"><span>SHA-256 enregistré</span><span>{integrity.result.expectedChecksum}</span></li>}
                            {integrity.result.status === 'mismatch' && integrity.result.actualChecksum && <li className="archive-integrity-hash"><span>SHA-256 calculé</span><span>{integrity.result.actualChecksum}</span></li>}
                          </ul>
                        </details>}
                      </>}
                    </section>

                    <section className="archive-detail-card" aria-labelledby="archive-identification-heading">
                      <h5 id="archive-identification-heading">Identification</h5>
                      <ul className="archive-detail-list">
                        <li>
                          <span>Plan</span>
                          <span>{planLabel}</span>
                        </li>
                        <li>
                          <span>Dossier</span>
                          <span>{folderLabel}</span>
                        </li>
                        <li>
                          <span>Profil d'archive</span>
                          <span>{profileLabel}</span>
                        </li>
                        <li>
                          <span>Service producteur</span>
                          <span>{serviceLabel}</span>
                        </li>
                        <li>
                          <span>Validation documentaire</span>
                          <span className="archive-detail-phase-value">{validationLabels[validationStatus] || formatValue(validationStatus)}</span>
                        </li>
                        {isTrashed && <li>
                          <span>Emplacement</span>
                          <span>Corbeille</span>
                        </li>}
                      </ul>
                    </section>

                    <section className="archive-detail-card" aria-labelledby="archive-conservation-heading">
                      <h5 id="archive-conservation-heading">Conservation et accès</h5>
                      <ul className="archive-detail-list">
                        <li><span>État</span><span className="archive-detail-phase-value">{conservationLabels[conservationStatus] || formatValue(conservationStatus)}</span></li>
                        <li><span>Confidentialité</span><span>{confidentialityLabel}</span></li>
                        <li><span>Règle de conservation</span><span>{regleLabel}</span></li>
                        {retentionRuleDiffersFromProfile && <li className="archive-retention-review"><span>À vérifier</span><span>Règle différente de celle prévue par défaut pour ce profil.</span></li>}
                        {retentionItems.map(([key, value]) => <li key={key}><span>{key}</span><span>{formatValue(value)}</span></li>)}
                        {doc.retentionDue && <li className="archive-retention-alert">
                          <span>Échéance</span>
                          <strong>À traiter</strong>
                        </li>}
                        <li>
                          <span>Début de conservation</span>
                          <span>{formatValue(doc.retentionStartAt)}</span>
                        </li>
                        <li>
                          <span>Fin de conservation</span>
                          <span>{formatValue(doc.retentionEndAt)}</span>
                        </li>
                      </ul>
                    </section>

                    <details className="archive-detail-card archive-detail-disclosure">
                      <summary><span>Métadonnées complémentaires</span><span className="archive-detail-count">{combinedMetadata.length} champ{combinedMetadata.length > 1 ? 's' : ''}</span></summary>
                      {combinedMetadata.length === 0 ? <div className="archive-empty">Aucune métadonnée complémentaire.</div> :
                        <ul className="archive-detail-list">
                          {combinedMetadata.map(([key, value]) => <li key={key}><span>{key}</span><span>{formatValue(value)}</span></li>)}
                        </ul>}
                    </details>

                    <section className="archive-detail-card" aria-labelledby="archive-history-heading">
                      <div className="archive-detail-card-header">
                        <h5 id="archive-history-heading">Historique</h5>
                        <div className="archive-trace-actions">
                          <button
                            className="btn btn-outline-secondary btn-xs btn-file-action"
                            type="button"
                            onClick={handleExportEvents}
                            disabled={!doc || !canDownload}
                          >
                            Exporter
                          </button>
                        </div>
                      </div>
                      <ul className="archive-detail-list archive-detail-list-compact archive-history-facts">
                        <li><span>Versions</span><span>{doc.versionCount ?? 0}</span></li>
                        <li><span>Créée le</span><span>{formatValue(doc.createdAt)}</span></li>
                        <li><span>Mise à jour le</span><span>{formatValue(doc.updatedAt)}</span></li>
                      </ul>
                      {lifeCycleEvents.length === 0 ? (
                        <div className="archive-empty">Aucun événement enregistré.</div>
                      ) : (
                        <ul className="archive-lifecycle-list">
                          {previewEvents.map((event) => {
                            const statusLabel = formatEventStatus(event);
                            return (
                              <li
                                key={event.id || `${event.type}-${event.eventDate}`}
                                className="archive-lifecycle-item"
                              >
                                <div className="archive-lifecycle-header">
                                  <span className="archive-lifecycle-type">
                                    {formatEventType(event.type)}
                                  </span>
                                  <span className="archive-lifecycle-date">
                                    {formatValue(event.eventDate)}
                                  </span>
                                </div>
                                {statusLabel && (
                                  <div className="archive-lifecycle-status">{statusLabel}</div>
                                )}
                                <div className="archive-lifecycle-user">
                                  Par {formatEventAuthor(event)}
                                </div>
                                {event.comment && (
                                  <div className="archive-lifecycle-comment">{event.comment}</div>
                                )}
                              </li>
                            );
                          })}
                        </ul>
                      )}
                      {lifeCycleEvents.length > 0 && <button type="button" className="archive-history-link" onClick={() => openEventDetails()}>
                        Voir le journal complet <i className="ri-arrow-right-line" aria-hidden="true" />
                      </button>}
                    </section>
                  </div>

                  <div className="archive-preview">
                    <div className="archive-preview-header">
                      <div>
                        <div className="archive-preview-title">Archive</div>
                        <div className="archive-preview-sub">
                          {doc.latestVersion?.originalName || 'Aucun fichier chargé'}
                        </div>
                      </div>
                      <div className="archive-preview-controls">
                        {doc.latestVersion && (
                          <div className="archive-preview-meta">
                            <span>V{doc.latestVersion.versionNumber}</span>
                            <span>{formatFileSize(doc.latestVersion.fileSize)}</span>
                          </div>
                        )}
                        <div className="archive-preview-rotate">
                          <button
                            className="btn btn-outline-secondary btn-sm"
                            type="button"
                            onClick={rotateLeft}
                            disabled={!isRotatable}
                            title="Pivoter à gauche"
                          >
                            <i className="ri-anticlockwise-2-line"></i>
                          </button>
                          <button
                            className="btn btn-outline-secondary btn-sm"
                            type="button"
                            onClick={rotateRight}
                            disabled={!isRotatable}
                            title="Pivoter à droite"
                          >
                            <i className="ri-clockwise-2-line"></i>
                          </button>
                          <button
                            className="btn btn-outline-secondary btn-sm"
                            type="button"
                            onClick={resetRotation}
                            disabled={!isRotatable || rotation === 0}
                          >
                            Réinitialiser
                          </button>
                        </div>
                      </div>
                    </div>
                    <div className="archive-preview-body">
                      {previewLoading ? (
                        SimpleSpinner()
                      ) : previewError ? (
                        <div className="archive-preview-empty">{previewError}</div>
                      ) : !doc.hasFile ? (
                        <div className="archive-preview-empty">Aucun fichier à afficher.</div>
                      ) : isPdf ? (
                        <div className="archive-preview-pdf">
                          <SecurePdfViewer file={previewBlob} />
                        </div>
                      ) : isImage ? (
                        <div className="archive-preview-image-container">
                          <img
                            className="archive-preview-image"
                            src={previewUrl}
                            alt={doc.title}
                            style={{ transform: `rotate(${rotation}deg)` }}
                          />
                        </div>
                      ) : (
                        <div className="archive-preview-empty">
                          Aperçu indisponible pour ce type de fichier.
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
      <Modal isOpen={correctionOpen} toggle={() => setCorrectionOpen(false)} className="modal-close">
        <ModalHeader toggle={() => setCorrectionOpen(false)}>Demander une correction</ModalHeader>
        <ModalBody>
          <label htmlFor="archive-correction-reason" className="form-label">Motif de correction</label>
          <textarea
            id="archive-correction-reason"
            className="form-control"
            rows="4"
            value={correctionReason}
            onChange={(event) => setCorrectionReason(event.target.value)}
            placeholder="Indiquez les éléments à corriger"
            maxLength={2000}
          />
        </ModalBody>
        <ModalFooter>
          <Button color="secondary" onClick={() => setCorrectionOpen(false)}>Annuler</Button>
          <Button color="primary" disabled={statusSaving || !correctionReason.trim()} onClick={() => handleStatusChange('À corriger', correctionReason.trim())}>Envoyer</Button>
        </ModalFooter>
      </Modal>
      <Modal isOpen={eventModalOpen} toggle={closeEventDetails} className="modal-close">
        <ModalHeader toggle={closeEventDetails}>Détails des événements</ModalHeader>
        <ModalBody>
          {!selectedEvent ? (
            lifeCycleEvents.length === 0 ? (
              <div className="archive-empty">Aucun événement enregistré.</div>
            ) : (
              <div className="archive-event-modal-list">
                {lifeCycleEvents.map((event) => (
                  <div
                    key={event.id || `${event.type}-${event.eventDate}`}
                    className="archive-event-modal-item"
                  >
                    <div className="archive-event-modal">
                      <div className="archive-event-modal-header">
                        <span className="archive-event-modal-type">
                          {formatEventType(event.type)}
                        </span>
                        <span className="archive-event-modal-date">
                          {formatValue(event.eventDate)}
                        </span>
                      </div>
                      <ul className="archive-event-meta">
                        <li>
                          <span>Utilisateur</span>
                          <span>{formatEventAuthor(event)}</span>
                        </li>
                        {formatEventStatus(event) && (
                          <li>
                            <span>Statut</span>
                            <span>{formatEventStatus(event)}</span>
                          </li>
                        )}
                        {event.comment && (
                          <li>
                            <span>Commentaire</span>
                            <span>{formatValue(event.comment)}</span>
                          </li>
                        )}
                      </ul>
                      <div className="archive-event-section-title">Détails</div>
                      {renderEventMetadata(event.metadata)}
                    </div>
                  </div>
                ))}
              </div>
            )
          ) : (
            <div className="archive-event-modal">
              <div className="archive-event-modal-header">
                <span className="archive-event-modal-type">
                  {formatEventType(selectedEvent.type)}
                </span>
                <span className="archive-event-modal-date">
                  {formatValue(selectedEvent.eventDate)}
                </span>
              </div>
              <ul className="archive-event-meta">
                <li>
                  <span>Utilisateur</span>
                  <span>{formatEventAuthor(selectedEvent)}</span>
                </li>
                {formatEventStatus(selectedEvent) && (
                  <li>
                    <span>Statut</span>
                    <span>{formatEventStatus(selectedEvent)}</span>
                  </li>
                )}
                {selectedEvent.comment && (
                  <li>
                    <span>Commentaire</span>
                    <span>{formatValue(selectedEvent.comment)}</span>
                  </li>
                )}
              </ul>
              <div className="archive-event-section-title">Détails</div>
              {renderEventMetadata(selectedEvent.metadata)}
            </div>
          )}
        </ModalBody>
        <ModalFooter>
          <Button color="secondary" onClick={closeEventDetails}>
            Fermer
          </Button>
        </ModalFooter>
      </Modal>
      <Modal isOpen={sheetOpen} toggle={() => setSheetOpen(false)} size="xl" className="archive-sheet-modal">
        <ModalHeader toggle={() => setSheetOpen(false)}>Fiche récapitulative de l’archive</ModalHeader>
        <ModalBody>
          {doc && <article className="archive-sheet">
            <header className="archive-sheet-title">
              <p>Syidocs Archivage · Fiche récapitulative</p>
              <h2>{doc.title || 'Archive'}</h2>
              <span>Référence : {formatValue(doc.reference)} · Éditée le {new Date().toLocaleString('fr-FR')}</span>
            </header>
            <section className="archive-sheet-section">
              <h3>Intégrité du fichier</h3>
              <dl>
                <dt>Résultat</dt><dd>{integrity.error || (integrity.result?.status === 'verified' && integrity.result.baselineEstablishedAt ? `Intégrité vérifiée depuis la référence du ${formatIntegrityDate(integrity.result.baselineEstablishedAt)}` : integrityLabels[integrity.result?.status] || 'Contrôle non disponible')}</dd>
                <dt>Version contrôlée</dt><dd>{integrity.result?.versionNumber != null ? `v${integrity.result.versionNumber}` : '—'}</dd>
                <dt>{['no_checksum', 'no_file', 'unavailable'].includes(integrity.result?.status) ? 'Dernière tentative' : 'Dernier contrôle'}</dt><dd>{formatIntegrityDate(integrity.result?.checkedAt)}</dd>
                {integrity.result?.baselineEstablishedAt && <><dt>Référence établie le</dt><dd>{formatIntegrityDate(integrity.result.baselineEstablishedAt)}</dd></>}
                {integrity.result?.expectedChecksum && <><dt>SHA-256 enregistré</dt><dd className="archive-sheet-hash">{integrity.result.expectedChecksum}</dd></>}
                {integrity.result?.status === 'mismatch' && integrity.result.actualChecksum && <><dt>SHA-256 calculé</dt><dd className="archive-sheet-hash">{integrity.result.actualChecksum}</dd></>}
              </dl>
              {integrity.result?.status === 'no_checksum' && <p className="archive-sheet-note">Aucune empreinte enregistrée : cette fiche ne permet pas de conclure sur l’intégrité du fichier.</p>}
            </section>
            <section className="archive-sheet-section">
              <h3>Identification</h3>
              <dl>
                <dt>Plan</dt><dd>{planLabel}</dd>
                <dt>Dossier</dt><dd>{folderLabel}</dd>
                <dt>Profil d’archive</dt><dd>{profileLabel}</dd>
                <dt>Service producteur</dt><dd>{serviceLabel}</dd>
                <dt>Validation documentaire</dt><dd>{validationLabels[validationStatus] || formatValue(validationStatus)}</dd>
                {isTrashed && <><dt>Emplacement</dt><dd>Corbeille</dd></>}
              </dl>
            </section>
            <section className="archive-sheet-section">
              <h3>Conservation et accès</h3>
              <dl>
                <dt>État</dt><dd>{conservationLabels[conservationStatus] || formatValue(conservationStatus)}</dd>
                <dt>Confidentialité</dt><dd>{confidentialityLabel}</dd>
                <dt>Règle de conservation</dt><dd>{regleLabel}</dd>
                {ruleContextItems.map(([key, value]) => <Fragment key={key}><dt>{key}</dt><dd>{formatValue(value)}</dd></Fragment>)}
                {doc.dispositionHoldAt && <><dt>Disposition suspendue depuis</dt><dd>{formatValue(doc.dispositionHoldAt)} · {doc.dispositionHoldReason}</dd></>}
                {retentionItems.map(([key, value]) => <Fragment key={key}><dt>{key}</dt><dd>{formatValue(value)}</dd></Fragment>)}
                {retentionRuleDiffersFromProfile && <><dt>À vérifier</dt><dd>Règle différente de celle prévue par défaut pour ce profil.</dd></>}
                {doc.retentionDue && <><dt>Échéance</dt><dd>À traiter</dd></>}
                <dt>Début de conservation</dt><dd>{formatValue(doc.retentionStartAt)}</dd>
                <dt>Fin de conservation</dt><dd>{formatValue(doc.retentionEndAt)}</dd>
              </dl>
            </section>
            <section className="archive-sheet-section">
              <h3>Métadonnées complémentaires</h3>
              {combinedMetadata.length ? <dl>{combinedMetadata.map(([key, value]) => <Fragment key={key}><dt>{key}</dt><dd>{formatValue(value)}</dd></Fragment>)}</dl> : <p>Aucune métadonnée complémentaire.</p>}
            </section>
            <section className="archive-sheet-section">
              <h3>Historique</h3>
              <dl>
                <dt>Versions</dt><dd>{doc.versionCount ?? 0}</dd>
                <dt>Créée le</dt><dd>{formatValue(doc.createdAt)}</dd>
                <dt>Mise à jour le</dt><dd>{formatValue(doc.updatedAt)}</dd>
              </dl>
              {lifeCycleEvents.length ? <ol className="archive-sheet-events">{lifeCycleEvents.map((event) => <li key={event.id || `${event.type}-${event.eventDate}`}><strong>{formatEventType(event.type)}</strong><span>{formatValue(event.eventDate)} · {formatEventAuthor(event)}</span>{event.comment && <p>{event.comment}</p>}{renderEventMetadata(event.metadata)}</li>)}</ol> : <p>Aucun événement enregistré.</p>}
            </section>
            <p className="archive-sheet-footnote">Cette fiche présente les données disponibles au moment de son édition. Le fichier conservé et son journal dans Syidocs demeurent les références à consulter.</p>
          </article>}
        </ModalBody>
        <ModalFooter>
          <Button color="secondary" onClick={() => setSheetOpen(false)}>Fermer</Button>
          <Button color="primary" onClick={() => window.print()}><i className="ri-printer-line" aria-hidden="true" /> Imprimer</Button>
        </ModalFooter>
      </Modal>
    </Fragment>
  );
};

export default ArchiveDetails;
